<template>
  <div
    v-click-outside="clickOutsideConfig"
    :data-testid="testid"
    class="drop-down-container"
    :class="{ visible: open }"
  >
    <button class="trigger" type="button" @click="toggle">
      <slot name="trigger"></slot>
    </button>

    <transition
      name="expand"
      @enter="enter"
      @after-enter="afterEnter"
      @leave="leave"
    >
      <div v-show="open" class="content" :data-testid="testid + '-content'">
        <slot name="content"></slot>
      </div>
    </transition>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import vClickOutside from '@/directives/clickOutside'

defineOptions({ name: 'DropDown' })

const props = withDefaults(
  defineProps<{
    trigger?: boolean
    expanded?: boolean
    height?: string
    clickOutside?: boolean
    testid?: string
  }>(),
  {
    trigger: true,
    height: 'auto',
    clickOutside: true,
    testid: '',
  },
)

const emit = defineEmits<{
  toggle: [open: boolean]
}>()

const open = ref(false)

onMounted(() => {
  open.value = !!props.expanded
})

watch(
  () => props.expanded,
  (value) => {
    open.value = !!value
  },
)

const clickOutsideConfig = computed(() => ({
  handler: tryClose,
  disabled: !props.clickOutside,
}))

function toggle(): void {
  if (props.trigger !== false) {
    open.value = !open.value
    emit('toggle', open.value)
  }
}

function tryClose(): void {
  if (props.clickOutside) {
    close()
  }
}

function close(): void {
  open.value = false
}

function enter(element: Element): void {
  const el = element as HTMLElement
  el.style.height = 'auto'
  const height = getComputedStyle(el).height
  el.style.height = '0'
  // eslint-disable-next-line
  window.getComputedStyle(el).height
  setTimeout(() => {
    el.style.height = props.height === 'auto' ? height : props.height
  })
}

function afterEnter(element: Element): void {
  const el = element as HTMLElement
  el.style.height = props.height
}

function leave(element: Element): void {
  const el = element as HTMLElement
  const height = getComputedStyle(el).height
  el.style.height = height
  // eslint-disable-next-line
  window.getComputedStyle(el).height
  setTimeout(() => {
    el.style.height = '0'
  })
}

// Parent components (MapSidebarWidgets, CustomGeoboundariesSidebarWidgets,
// MapSidebar) drive the expand state via a template ref — e.g.
// `this.$refs[key].open = value`. Exposing the `open` ref lets Vue's expose
// proxy unwrap it so those writes go through to `open.value`, preserving the
// Vue 2 `$refs[key].open` contract.
defineExpose({ open, close })
</script>
<style scoped lang="scss" src="./drop-down.scss"></style>
